import { arenas, fighters } from './content'
import {
  ELYON_AUDIO,
  ELYON_SHARED_PRESENTATION_MANIFEST,
  ELYON_VFX,
  elyonPublicAsset,
  sharedPresentationManifestUrl,
} from './elyonMedia'

type SharedFighter = { id: string; assetId: string; modelUrl: string }
type SharedArena = { id: string; landmarkId: string; landmarkUrl: string }
type SharedPresentationManifest = {
  schemaVersion: number
  authority: string
  contentVersion: string
  policy?: {
    productionPlaceholder?: string
    missingAuthoredAsset?: string
    modeAssetCollision?: string
    motionCompatibility?: string
  }
  referenceMotionProfile?: {
    animationDnaId?: string
    slots?: Record<string, string>
  }
  combatAudio?: Record<string, string>
  combatVfx?: Record<string, string>
  fighters?: SharedFighter[]
  arenas?: SharedArena[]
}

type ElyonFederationContract = {
  schema?: string
  universe?: string
  authorityRepository?: string
  canonicalPublicRoute?: string
  assetGateway?: string
  presentationManifest?: string
  presentationSchemaVersion?: number
  assetAuthority?: {
    collisionPolicy?: string
    missingBinaryPolicy?: string
    productionPlaceholderPolicy?: string
  }
  consumers?: Array<{ id?: string; repository?: string; runtimeAuthority?: string }>
}

const ELYON_FEDERATION_CONTRACT = '/contracts/elyon-content-federation.v2.json'

const expectedAudio: Record<string, string> = {
  swing: ELYON_AUDIO.combatSwing,
  hit: ELYON_AUDIO.combatHit,
  heavy: ELYON_AUDIO.combatCrit,
  special: ELYON_AUDIO.combatShockwave,
  hurt: ELYON_AUDIO.combatHurt,
  victory: ELYON_AUDIO.combatVictory,
  defeat: ELYON_AUDIO.combatDefeat,
  bossEntrance: ELYON_AUDIO.combatBossEntrance,
}

const expectedVfx: Record<string, string> = {
  hit: ELYON_VFX.hit,
  heavy: ELYON_VFX.heavy,
  special: ELYON_VFX.special,
  throw: ELYON_VFX.throw,
  block: ELYON_VFX.block,
  parry: ELYON_VFX.parry,
  guardBreak: ELYON_VFX.guardBreak,
  status: ELYON_VFX.status,
  downed: ELYON_VFX.downed,
}

const requiredMotionSlots = [
  'locomotion.idle',
  'locomotion.run',
  'locomotion.jump',
  'combat.light',
  'combat.heavy',
  'combat.special',
  'combat.throw',
  'combat.block',
  'combat.parry',
  'combat.dodge',
  'combat.hit',
  'combat.ko',
] as const

function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(`[ELYON shared presentation] ${message}`)
}


function fightCertifiedFighterOverlay(): SharedFighter[] {
  return fighters
    .filter(fighter => fighter.id === 'vaelthar-astral-architect')
    .map(fighter => ({ id: fighter.id, assetId: fighter.assetId, modelUrl: fighter.modelUrl }))
}

function sameRecord(actual: Record<string, string> | undefined, expected: Record<string, string>, label: string) {
  invariant(actual, `${label} missing`)
  for (const [key, value] of Object.entries(expected)) {
    invariant(actual[key] === value, `${label}.${key} drifted: expected ${value}, got ${actual[key] ?? '<missing>'}`)
  }
}

export function validateElyonFederationContract(value: unknown): ElyonFederationContract {
  invariant(value && typeof value === 'object', 'federation contract is not an object')
  const contract = value as ElyonFederationContract
  invariant(contract.schema === 'inyron.elyon.content-federation.v2', `unsupported federation schema ${String(contract.schema)}`)
  invariant(contract.universe === 'ELYON', `federation universe must be ELYON, got ${String(contract.universe)}`)
  invariant(contract.authorityRepository === 'inyron/Inyron', 'federation authority repository drifted')
  invariant(contract.canonicalPublicRoute === '/elyon', 'canonical public route drifted')
  invariant(contract.assetGateway === '/elyon/asset-cdn', 'canonical asset gateway drifted')
  invariant(
    contract.presentationManifest === `${contract.canonicalPublicRoute}${ELYON_SHARED_PRESENTATION_MANIFEST}`,
    'presentation manifest route drifted from the canonical Elyon public root',
  )
  invariant(contract.presentationSchemaVersion === 1, 'unsupported presentation schema version')
  invariant(contract.assetAuthority?.collisionPolicy === 'canonical-elyon-wins', 'canonical collision policy drifted')
  invariant(contract.assetAuthority?.missingBinaryPolicy === 'hard-404', 'missing binary policy must be hard-404')
  invariant(contract.assetAuthority?.productionPlaceholderPolicy === 'forbidden', 'production placeholder policy must be forbidden')

  const consumers = new Map((contract.consumers ?? []).map(consumer => [consumer.id, consumer]))
  invariant(consumers.get('portal-battle-arena')?.repository === 'inyron/Inyron', 'Battle Arena federation membership missing')
  invariant(consumers.get('rift-fighters')?.repository === 'artnuclear/Luta', 'Rift Fighters federation membership missing')
  invariant(consumers.get('rift-fighters')?.runtimeAuthority === 'FIGHTING_COMBAT', 'Fight runtime authority drifted')
  return contract
}

export function validateSharedPresentationManifest(value: unknown): SharedPresentationManifest {
  invariant(value && typeof value === 'object', 'manifest is not an object')
  const manifest = value as SharedPresentationManifest
  invariant(manifest.schemaVersion === 1, `unsupported schemaVersion ${String(manifest.schemaVersion)}`)
  invariant(manifest.authority === 'ELYON', `authority must be ELYON, got ${String(manifest.authority)}`)
  invariant(manifest.contentVersion.startsWith('elyon-main/'), 'contentVersion is not pinned to Elyon main lineage')
  invariant(manifest.policy?.productionPlaceholder === 'forbidden', 'production placeholder policy is not fail-closed')
  invariant(manifest.policy?.missingAuthoredAsset === 'fail-closed', 'missing authored asset policy is not fail-closed')
  invariant(manifest.policy?.modeAssetCollision === 'elyon-wins', 'mode collision policy must preserve Elyon')
  invariant(manifest.policy?.motionCompatibility === 'dna-or-rig-match-required', 'motion compatibility policy missing')

  sameRecord(manifest.combatAudio, expectedAudio, 'combatAudio')
  sameRecord(manifest.combatVfx, expectedVfx, 'combatVfx')

  const canonicalFighters = new Map((manifest.fighters ?? []).map(item => [item.id, item]))
  
  for (const overlay of fightCertifiedFighterOverlay()) canonicalFighters.set(overlay.id, overlay)
  invariant(canonicalFighters.size >= fighters.length, 'canonical fighter roster is incomplete')
  for (const fighter of fighters) {
    const canonical = canonicalFighters.get(fighter.id)
    invariant(canonical, `fighter ${fighter.id} missing from canonical manifest`)
    invariant(canonical.assetId === fighter.assetId, `fighter ${fighter.id} assetId drifted`)
    invariant(canonical.modelUrl === fighter.modelUrl, `fighter ${fighter.id} model provenance drifted`)
  }

  const canonicalArenas = new Map((manifest.arenas ?? []).map(item => [item.id, item]))
  invariant(canonicalArenas.size >= arenas.length, 'canonical arena registry is incomplete')
  for (const arena of arenas) {
    const canonical = canonicalArenas.get(arena.id)
    invariant(canonical, `arena ${arena.id} missing from canonical manifest`)
    invariant(canonical.landmarkId === arena.landmarkId, `arena ${arena.id} landmarkId drifted`)
    invariant(canonical.landmarkUrl === arena.landmarkUrl, `arena ${arena.id} landmark provenance drifted`)
  }

  const motionProfile = manifest.referenceMotionProfile
  invariant(motionProfile?.animationDnaId, 'reference motion profile missing animation DNA')
  invariant(motionProfile.slots, 'reference motion profile missing semantic slots')
  for (const slot of requiredMotionSlots) {
    const url = motionProfile.slots[slot]
    invariant(typeof url === 'string' && url.startsWith('/assets/animations/hero/'), `motion slot ${slot} has no canonical authored URL`)
    invariant(url.endsWith('.glb'), `motion slot ${slot} must resolve to authored GLB`)
  }

  return manifest
}

async function fetchJson(url: string, label: string): Promise<unknown> {
  const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } })
  invariant(response.ok, `${label} request failed ${response.status} ${response.statusText} at ${url}`)
  return response.json()
}

export async function loadAndValidateSharedPresentation(): Promise<SharedPresentationManifest> {
  const federationUrl = elyonPublicAsset(ELYON_FEDERATION_CONTRACT)
  const federation = validateElyonFederationContract(await fetchJson(federationUrl, 'federation contract'))
  const manifestUrl = sharedPresentationManifestUrl()
  const manifest = validateSharedPresentationManifest(await fetchJson(manifestUrl, 'presentation manifest'))
  invariant(manifest.schemaVersion === federation.presentationSchemaVersion, 'presentation schema disagrees with federation contract')
  return manifest
}
